import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getStoreInfo,
  formatDisplay,
  type StoreInfo,
  type Vendedor,
} from '@/services/store.service';
import { useVendedorGuardado, type OrigenVendedor } from '@/services/vendedor';

const FALLBACK: StoreInfo = {
  phone: '5350378286',
  whatsapp: '5350378286',
  phoneDisplay: '+53 5037 8286',
  email: null,
  vendedores: [],
};

export function useStoreInfo() {
  const [base, setBase] = useState<StoreInfo>(FALLBACK);
  const [loading, setLoading] = useState(true);
  const guardado = useVendedorGuardado();

  const refresh = useCallback(() => {
    getStoreInfo()
      .then((data) => setBase(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    refresh();

    // Refresh every 30 seconds so API changes appear quickly
    const interval = setInterval(refresh, 30_000);

    // Refresh immediately when user comes back to the tab
    const onVisible = () => {
      if (!document.hidden) refresh();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refresh]);

  // Vendedor asignado (por su link o elegido por el cliente). Si el código ya
  // no existe en el Sheet, se ignora y se usa el número general.
  const vendedor: Vendedor | null = useMemo(
    () => base.vendedores.find((v) => v.codigo === guardado?.codigo) ?? null,
    [base.vendedores, guardado?.codigo],
  );
  const origenVendedor: OrigenVendedor | null = vendedor ? guardado?.origen ?? null : null;

  // Todos los botones de WhatsApp de la tienda van al vendedor asignado.
  const info = useMemo(
    () =>
      vendedor
        ? {
            ...base,
            phone: vendedor.whatsapp,
            whatsapp: vendedor.whatsapp,
            phoneDisplay: formatDisplay(vendedor.whatsapp),
          }
        : base,
    [base, vendedor],
  );

  return { info, loading, vendedor, origenVendedor, vendedores: base.vendedores };
}
